import { useEffect, useState } from 'react';
import FolderPanel from '../components/FolderPanel';
import FileViewer from '../components/FileViewer';
import { Box, CircularProgress, Snackbar, Modal, Typography } from '@mui/material';
import Header from '../components/Header';
import Menu from '../components/Menu';
import { useLocation } from 'react-router-dom';
import { getAllFolders, getFolderAccess, moveFolder, reorderFolders } from '../api/folderAPI';
import { getUser } from '../api/userAPI';
import ProtectedFileModal from '../components/ProtectedFileModal';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import {
  createFile,
  downloadFile,
  deleteFile,
  updateFileName,
  getFileAccess,
  moveFile
} from '../api/fileAPI';
import ModalRenameFile from '../pages/modal/ModalRenameFile';
import ModalFilePermissions from '../pages/modal/ModalFilePermissions';
import useTokenAutoLogout from '../hooks/useTokenAutoLogout';
import { useTranslation } from 'react-i18next';
import { FolderData } from '../types/types';

const COMPANY_ID = process.env.REACT_APP_COMPANY_ID;

const Viewer = () => {
  const location = useLocation();
  const email = (location.state as { email: string })?.email || '';
  const [folders, setFolders] = useState<FolderData[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<FolderData | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [user, setUser] = useState<{
    userId: string;
    userName: string;
    userSurname: string;
    accessType: 'ADMIN' | 'MEMBER';
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [viewedFileName, setViewedFileName] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [fileToRename, setFileToRename] = useState<{ id: string; name: string } | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('info');
  const [filePermissionTarget, setFilePermissionTarget] = useState<{
    fileId: string;
    fileName: string;
    view: boolean;
    download: boolean;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [search, setSearch] = useState('');
  const { t, i18n } = useTranslation();

  useTokenAutoLogout();

  const filterFoldersByAccess = (
    folderList: FolderData[],
    allowedIds: Set<string>
  ): FolderData[] => {
    return folderList
      .map(folder => {
        const filteredSubfolders = filterFoldersByAccess(
          folder.subfolders || [],
          allowedIds
        );
        const hasAccess = allowedIds.has(folder.folderId);

        if (hasAccess || filteredSubfolders.length > 0) {
          return {
            ...folder,
            subfolders: filteredSubfolders,
            files: hasAccess ? folder.files : [],
          };
        }

        return null;
      })
      .filter((f): f is FolderData => f !== null);
  };

  const getFirstAccessibleFolder = (folderList: FolderData[]): FolderData | null => {
    for (const folder of folderList) {
      if (folder.files.length > 0) return folder;
      const sub = getFirstAccessibleFolder(folder.subfolders || []);
      if (sub) return sub;
    }
    return folderList.length > 0 ? folderList[0] : null;
  };

  const findFolderById = (folderList: FolderData[], id: string): FolderData | null => {
    for (const folder of folderList) {
      if (folder.folderId === id) return folder;
      const foundInSub = findFolderById(folder.subfolders || [], id);
      if (foundInSub) return foundInSub;
    }
    return null;
  };

  const getFolderPath = (
    folderList: FolderData[],
    targetId: string,
    path: string[] = []
  ): string[] => {
    for (const folder of folderList) {
      if (folder.folderId === targetId) {
        return [...path, folder.folderName];
      }
      if (folder.subfolders && folder.subfolders.length > 0) {
        const subPath = getFolderPath(
          folder.subfolders,
          targetId,
          [...path, folder.folderName]
        );
        if (subPath.length > 0) return subPath;
      }
    }
    return [];
  };

  const refreshFoldersForUser = async (
    userId: string,
    preferredFolderId: string | null = selectedFolderId
  ) => {
    const [allFolders, accessList] = await Promise.all([
      getAllFolders(),
      getFolderAccess(userId),
    ]);

    const allowedFolderIds = new Set(
      accessList.filter(a => a.access).map(a => a.folderId)
    );

    const filteredFolders = filterFoldersByAccess(allFolders, allowedFolderIds);
    setFolders(filteredFolders);

    const preferred = preferredFolderId
      ? findFolderById(filteredFolders, preferredFolderId)
      : null;

    const nextSelected = preferred || getFirstAccessibleFolder(filteredFolders);

    setSelectedFolder(nextSelected);
    setSelectedFolderId(nextSelected?.folderId || null);
  };

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  useEffect(() => {
    const fetchUserAndFolders = async () => {
      setLoading(true);
      try {
        const userData = await getUser(email, COMPANY_ID!);
        setUser({
          userId: userData.userId,
          userName: userData.userName,
          userSurname: userData.userSurname,
          accessType: userData.userAccess?.accessType || 'MEMBER',
        });

        const [allFolders, accessList] = await Promise.all([
          getAllFolders(),
          getFolderAccess(userData.userId),
        ]);

        const allowedFolderIds = new Set(
          accessList.filter(a => a.access).map(a => a.folderId)
        );

        const filteredFolders = filterFoldersByAccess(allFolders, allowedFolderIds);
        setFolders(filteredFolders);

        const firstFolder = getFirstAccessibleFolder(filteredFolders);
        if (firstFolder) {
          setSelectedFolder(firstFolder);
          setSelectedFolderId(firstFolder.folderId);
        }
      } catch (err) {
        console.error('Error loading user or folders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndFolders();
  }, [email]);

  const handleEditFile = (fileName: string) => {
    const file = selectedFolder?.files.find(f => f.fileName === fileName);
    if (file) {
      setFileToRename({ id: file.fileId, name: file.fileName });
    }
  };

  const handleConfirmRenameFile = async (newName: string) => {
    if (!fileToRename || !selectedFolderId) return;

    try {
      await updateFileName(fileToRename.id, newName, selectedFolderId, COMPANY_ID!);

      setFolders(prev => prev.map(folder => {
        if (folder.folderId !== selectedFolderId) return folder;
        const updatedFiles = folder.files.map(file =>
          file.fileId === fileToRename.id ? { ...file, fileName: newName } : file
        );
        return { ...folder, files: updatedFiles };
      }));

      setSelectedFolder(prev =>
        prev
          ? {
              ...prev,
              files: prev.files.map(file =>
                file.fileId === fileToRename.id
                  ? { ...file, fileName: newName }
                  : file
              )
            }
          : null
      );

      setSnackbarMessage('File renamed successfully.');
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error renaming file:', err);
      setSnackbarMessage(`Failed to rename file: ${err.message}`);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setFileToRename(null);
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!selectedFolderId || !selectedFolder) return;

    const fileToDelete = selectedFolder.files.find(f => f.fileName === fileName);
    if (!fileToDelete) {
      setSnackbarMessage(`File "${fileName}" not found.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await deleteFile(fileToDelete.fileId, COMPANY_ID!, selectedFolderId);

      setFolders(prevFolders => {
        const updated = removeFileFromFolders(
          prevFolders,
          selectedFolderId,
          fileToDelete.fileId
        );
        const updatedSelected = findFolderById(updated, selectedFolderId);
        if (updatedSelected) {
          setSelectedFolder(updatedSelected);
        }
        return updated;
      });

      setSnackbarMessage(`File "${fileName}" deleted successfully.`);
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error deleting file:', err);
      setSnackbarMessage(`Error deleting file "${fileName}": ${err.message}`);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleViewFile = (fileName: string) => {
    if (!selectedFolderId || !selectedFolder) return;

    setViewedFileName(fileName);
    setIsModalOpen(true);
  };

  const handleDownloadFile = async (fileName: string) => {
    try {
      setDownloading(true);
      const url = await downloadFile(fileName, selectedFolderId || '', email);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(`Error downloading file "${fileName}":`, err);
      setSnackbarMessage(`Error downloading file "${fileName}"`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    setDownloading(false);
  };

  const addFileToFolder = (
    folderList: FolderData[],
    targetId: string,
    newFile: any
  ): FolderData[] => {
    return folderList.map(folder => {
      if (folder.folderId === targetId) {
        return {
          ...folder,
          files: [...folder.files, newFile]
        };
      }
      if (folder.subfolders.length > 0) {
        return {
          ...folder,
          subfolders: addFileToFolder(folder.subfolders, targetId, newFile)
        };
      }
      return folder;
    });
  };

  const removeFileFromFolders = (
    folderList: FolderData[],
    targetFolderId: string,
    fileId: string
  ): FolderData[] => {
    return folderList.map(folder => {
      if (folder.folderId === targetFolderId) {
        return {
          ...folder,
          files: folder.files.filter(f => f.fileId !== fileId)
        };
      }
      if (folder.subfolders && folder.subfolders.length > 0) {
        return {
          ...folder,
          subfolders: removeFileFromFolders(
            folder.subfolders,
            targetFolderId,
            fileId
          )
        };
      }
      return folder;
    });
  };

  const handleDropFiles = async (files: FileList) => {
    if (!selectedFolderId) return;

    setUploading(true);

    for (const file of Array.from(files)) {
      const fileURL = `s3://${COMPANY_ID}/${selectedFolderId}/${file.name}`;

      try {
        const createdFile = await createFile(file, fileURL, selectedFolderId);

        setFolders(prevFolders => {
          const updatedFolders = addFileToFolder(
            prevFolders,
            selectedFolderId,
            createdFile
          );
          const updatedSelected = findFolderById(
            updatedFolders,
            selectedFolderId
          );
          if (updatedSelected) {
            setSelectedFolder(updatedSelected);
          }
          return updatedFolders;
        });
      } catch (err: any) {
        console.error(`Error uploading file ${file.name}:`, err);
        setSnackbarMessage(`Error uploading file "${file.name}": ${err.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
    setUploading(false);
  };

  const handleMoveFile = async (
    fileId: string,
    sourceFolderId: string,
    targetFolderId: string
  ) => {
    if (!user || sourceFolderId === targetFolderId) return;

    setMoving(true);
    try {
      await moveFile(fileId, targetFolderId);
      await refreshFoldersForUser(user.userId, selectedFolderId);

      setSnackbarMessage('File moved successfully.');
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error moving file:', err);
      setSnackbarMessage(`Could not move file: ${err.message}`);
      setSnackbarSeverity('error');
    } finally {
      setMoving(false);
      setSnackbarOpen(true);
    }
  };

  const handleMoveFolder = async (
    folderId: string,
    targetParentFolderId: string | null
  ) => {
    if (!user) return;

    if (targetParentFolderId === folderId) {
      setSnackbarMessage('A folder cannot be moved inside itself.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const sourceFolder = findFolderById(folders, folderId);
    if (
      sourceFolder &&
      targetParentFolderId &&
      findFolderById(sourceFolder.subfolders || [], targetParentFolderId)
    ) {
      setSnackbarMessage('A folder cannot be moved inside one of its subfolders.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setMoving(true);
    try {
      await moveFolder(folderId, targetParentFolderId);
      await refreshFoldersForUser(user.userId, selectedFolderId);

      setSnackbarMessage('Folder moved successfully.');
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error moving folder:', err);
      setSnackbarMessage(`Could not move folder: ${err.message}`);
      setSnackbarSeverity('error');
    } finally {
      setMoving(false);
      setSnackbarOpen(true);
    }
  };

  const handleReorderFolders = async (
    parentFolderId: string | null,
    orderedFolderIds: string[]
  ) => {
    if (!user) return;

    setMoving(true);
    try {
      await reorderFolders(parentFolderId, orderedFolderIds);
      await refreshFoldersForUser(user.userId, selectedFolderId);

      setSnackbarMessage('Folder order updated successfully.');
      setSnackbarSeverity('success');
    } catch (err: any) {
      console.error('Error reordering folders:', err);
      setSnackbarMessage(`Could not reorder folders: ${err.message}`);
      setSnackbarSeverity('error');
    } finally {
      setMoving(false);
      setSnackbarOpen(true);
    }
  };

  const handlePermissions = async (fileName: string) => {
    const file = selectedFolder?.files.find(f => f.fileName === fileName);
    if (!file) return;

    try {
      const access = await getFileAccess(file.fileId);
      setFilePermissionTarget({
        fileId: file.fileId,
        fileName: file.fileName,
        view: access.view,
        download: access.download,
      });
    } catch (err) {
      console.error('Error fetching file access:', err);
      setSnackbarMessage('Could not fetch permissions.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const collectFilesRecursively = (
    folderList: FolderData[],
    parentPath: string[] = []
  ): Array<{
    fileId: string;
    fileName: string;
    fileURL: string;
    fileAccess: { view: boolean; download: boolean };
    folderId: string;
    folderName: string;
    folderPath: string[];
  }> => {
    let result: Array<{
      fileId: string;
      fileName: string;
      fileURL: string;
      fileAccess: { view: boolean; download: boolean };
      folderId: string;
      folderName: string;
      folderPath: string[];
    }> = [];

    folderList.forEach(folder => {
      const currentPath = [...parentPath, folder.folderName];

      folder.files.forEach(file => {
        result.push({
          ...file,
          folderId: folder.folderId,
          folderName: folder.folderName,
          folderPath: currentPath,
        });
      });

      if (folder.subfolders && folder.subfolders.length > 0) {
        result = result.concat(
          collectFilesRecursively(folder.subfolders, currentPath)
        );
      }
    });

    return result;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header name={`${user?.userName || ''} ${user?.userSurname || ''}`} />
      <Menu
        folders={folders}
        refreshFolders={async () => {
          if (user) {
            await refreshFoldersForUser(user.userId, selectedFolderId);
          }
        }}
        accessType={user?.accessType || 'MEMBER'}
        search={search}
        setSearch={setSearch}
      />
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <Box sx={{ width: '30%', borderRight: '1px solid #ccc' }}>
          <FolderPanel
            folders={folders}
            selectedId={selectedFolder?.folderId || ''}
            accessType={user?.accessType || 'MEMBER'}
            onMoveFile={handleMoveFile}
            onMoveFolder={handleMoveFolder}
            onReorderFolders={handleReorderFolders}
            onSelect={(folderId) => {
              const folder = findFolderById(folders, folderId);
              if (folder) {
                setSelectedFolder(folder);
                setSelectedFolderId(folder.folderId);
              }
            }}
          />
        </Box>
        <Box sx={{ width: '70%', padding: 2 }}>
          {search.trim() !== '' ? (
            <FileViewer
              files={collectFilesRecursively(folders)
                .filter(file =>
                  file.fileName.toLowerCase().includes(search.toLowerCase())
                )
                .map(file => ({
                  fileId: file.fileId,
                  fileName: file.fileName,
                  fileURL: file.fileURL,
                  fileAccess: file.fileAccess,
                  folderId: file.folderId,
                  folderName: file.folderName,
                }))
              }
              folderPath={['Search Results']}
              folderId="search"
              onEdit={handleEditFile}
              onDelete={handleDeleteFile}
              onView={handleViewFile}
              onDownload={handleDownloadFile}
              onDrop={() => {}}
              onPermissions={handlePermissions}
              accessType={user?.accessType || 'MEMBER'}
            />
          ) : selectedFolder && (
            <FileViewer
              files={selectedFolder.files}
              folderPath={getFolderPath(folders, selectedFolder.folderId)}
              folderId={selectedFolder.folderId}
              onEdit={handleEditFile}
              onDelete={handleDeleteFile}
              onView={handleViewFile}
              onDownload={handleDownloadFile}
              onDrop={handleDropFiles}
              onPermissions={handlePermissions}
              accessType={user?.accessType || 'MEMBER'}
            />
          )}
        </Box>
      </Box>

      {isModalOpen && viewedFileName && selectedFolderId && (
        <ProtectedFileModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          companyId={COMPANY_ID!}
          folderId={selectedFolderId}
          fileName={viewedFileName}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>

      <Modal open={uploading}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">{t('uploadingFile')}</Typography>
        </Box>
      </Modal>

      <Modal open={downloading}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">{t('preparingFile')}</Typography>
        </Box>
      </Modal>

      <Modal open={moving}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">Moving...</Typography>
        </Box>
      </Modal>

      {fileToRename && (
        <ModalRenameFile
          open={Boolean(fileToRename)}
          fileName={fileToRename.name}
          onClose={() => setFileToRename(null)}
          onConfirm={handleConfirmRenameFile}
        />
      )}

      {filePermissionTarget && (
        <ModalFilePermissions
          key={filePermissionTarget.fileId}
          open={true}
          onClose={() => setFilePermissionTarget(null)}
          fileId={filePermissionTarget.fileId}
          fileName={filePermissionTarget.fileName}
          initialView={filePermissionTarget.view}
          initialDownload={filePermissionTarget.download}
        />
      )}
    </Box>
  );
};

export default Viewer;
